import { useCallback, useState } from 'react';

interface FileUploadProps {
  onFileLoaded: (content: string, fileName: string) => void;
}

export default function FileUpload({ onFileLoaded }: FileUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFile = useCallback(
    (file: File) => {
      setError(null);

      // Validate file type
      if (!file.name.endsWith('.md')) {
        setError('Please upload a .md (Markdown) file');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('File is too large. Maximum size is 5MB');
        return;
      }

      // Read file
      const reader = new FileReader();

      reader.onload = (e) => {
        const content = e.target?.result as string;
        onFileLoaded(content, file.name);
      };

      reader.onerror = () => {
        setError('Failed to read file');
      };

      reader.readAsText(file);
    },
    [onFileLoaded]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);

      const files = e.dataTransfer.files;
      if (files.length > 0) {
        handleFile(files[0]);
      }
    },
    [handleFile]
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        handleFile(files[0]);
      }
    },
    [handleFile]
  );

  return (
    <div style={{ padding: '40px', maxWidth: '600px', margin: '0 auto' }}>
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        style={{
          border: isDragging ? '3px dashed #3b82f6' : '2px dashed #cbd5e1',
          borderRadius: '12px',
          padding: '60px 40px',
          textAlign: 'center',
          background: isDragging ? '#eff6ff' : '#f8fafc',
          transition: 'all 0.2s ease',
          cursor: 'pointer',
        }}
      >
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>📄</div>
        <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#1e293b', marginBottom: '8px' }}>
          Upload Fractalized Planning Document
        </h2>
        <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '24px' }}>
          Drag and drop your .md file here, or click to browse
        </p>

        <label htmlFor="file-input">
          <div
            style={{
              display: 'inline-block',
              padding: '12px 24px',
              background: '#3b82f6',
              color: '#ffffff',
              borderRadius: '8px',
              fontWeight: 'bold',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            Choose File
          </div>
        </label>
        <input
          id="file-input"
          type="file"
          accept=".md"
          onChange={handleFileInput}
          style={{ display: 'none' }}
        />

        {error && (
          <div
            style={{
              marginTop: '16px',
              padding: '12px',
              background: '#fee2e2',
              border: '1px solid #fca5a5',
              borderRadius: '8px',
              color: '#dc2626',
              fontSize: '14px',
            }}
          >
            {error}
          </div>
        )}
      </div>

      <div style={{ marginTop: '24px', fontSize: '12px', color: '#64748b' }}>
        <p style={{ marginBottom: '8px' }}>
          <strong>Supported Format:</strong> Fractalized Q&A Markdown
        </p>
        <ul style={{ paddingLeft: '20px', lineHeight: '1.8' }}>
          <li>QUESTION blocks with [NodeID] format</li>
          <li>ANSWER blocks with Dependencies</li>
          <li>Hierarchical node structure (Root, L1, L2, L3)</li>
        </ul>
      </div>
    </div>
  );
}
